import { CSElement } from './core/CSElement';

/**
 * CSElement - Потокобезопасная библиотека для работы с графовыми структурами
 * Основной экспорт содержит только браузерно-совместимые модули
 */

// Основной класс
export { CSElement };
export default CSElement;

// Типы и интерфейсы
export {
  ICSElement,
  ICSElementPattern,
  ElementOptions,
  ElementValue,
  ElementEvent,
  ElementEventType,
  SerializationOptions,
  SerializedElement,
  ValidationRule,
  ValidationResult,
  ValidationError,
  QuerySelector,
  PersistenceAdapter,
  PerformanceMetrics
} from './types/interfaces';

// Типы плагинов
export {
  CSElementPlugin,
  LifecycleHooks,
  MiddlewareFunction,
  MiddlewareContext,
  MiddlewareConfig,
  MiddlewareResult,
  MiddlewareStats,
  MiddlewarePriority,
  AdvancedMiddlewareManager,
  PluginManager as IPluginManager,
  PluginOptions,
  PluginInfo,
  PluginEvent
} from './types/plugin-interfaces';

// Типы визуализации
export {
  VisualizationFormat,
  LayoutType,
  TreeDirection,
  NodeStyle,
  EdgeStyle,
  VisualizationOptions,
  VisualizationResult,
  VisualizationNode,
  VisualizationEdge,
  VisualizationGraph,
  VisualizationEngine,
  VisualizationManager as IVisualizationManager,
  VisualizationEvent,
  VisualizationTheme,
  FileExporter
} from './types/visualization-interfaces';

// Утилиты
export { AsyncLock } from './utils/AsyncLock';
export { generateId, deepClone, delay } from './utils/helpers';
export { ElementValidator, defaultValidator, ValidationType } from './utils/Validator';
export { QueryEngine, SelectorBuilder, CommonSelectors } from './utils/QueryEngine';
export { TypeScriptGenerator, generateTypesFromSchemas, generateTypesToFile } from './utils/TypeScriptGenerator';

// Batch Operations
export { BatchManager } from './batch/BatchManager';
export { DefaultBatchOperationFactory, DefaultBatchBuilder, BatchOperations } from './batch/BatchOperationFactory';
export {
  BatchOperationType,
  BatchPriority,
  BatchExecutionStrategy,
  BatchErrorMode,
  IBatchManager,
  BatchConfig,
  BatchResult,
  BatchProgress,
  BatchEventListener,
  BatchManagerStatistics,
  BatchOperationFactory,
  BatchBuilder
} from './types/batch-interfaces';
export type {
  BatchOperation,
  BatchExecutionContext,
  BatchOperationResult,
  BatchValidationResult,
  BatchStatistics
} from './types/batch-interfaces';

// Blueprint System
export { Blueprint } from './blueprint/Blueprint';
export { BlueprintBuilder } from './blueprint/BlueprintBuilder';
export { BlueprintManager } from './blueprint/BlueprintManager';
export * from './types/blueprint-interfaces';

// Система плагинов
export { PluginManager } from './core/PluginManager';
export { AdvancedMiddlewareManagerImpl } from './core/AdvancedMiddlewareManager';

// Система персистентности
export * from './types/persistence-interfaces';
export { MemoryStorageAdapter } from './persistence/MemoryStorageAdapter';
export { PersistenceManagerImpl } from './persistence/PersistenceManagerImpl';

// Браузерные адаптеры доступны только в браузерной версии:
// IndexedDBAdapter и LocalForageAdapter требуют браузерного окружения

// Система транзакций
export * from './types/transaction-interfaces';
export { LockManagerImpl } from './transactions/LockManagerImpl';
export { TransactionManagerImpl } from './transactions/TransactionManagerImpl';

// Встроенные плагины (только браузерно-совместимые)
export { VisualizationPlugin, visualizationPlugin } from './plugins/VisualizationPlugin';
export { ValidationPlugin, validationPlugin } from './plugins/ValidationPlugin';
export { CachePlugin, cachePlugin } from './plugins/CachePlugin';
export { MetricsPlugin, metricsPlugin } from './plugins/MetricsPlugin';
export { TransformPlugin, transformPlugin } from './plugins/TransformPlugin';
export { AnalyticsPlugin } from './plugins/AnalyticsPlugin';
export { DevToolsPlugin, devToolsPlugin } from './plugins/DevToolsPlugin';
export { LoggingPlugin } from './plugins/LoggingPlugin';

// Node.js специфичные плагины доступны через отдельные импорты:
// import { BackupPlugin } from 'cs-element/plugins/backup';
// import { CompressionPlugin } from 'cs-element/plugins/compression';
// import { SecurityPlugin } from 'cs-element/plugins/security';
// import { NetworkPlugin } from 'cs-element/plugins/network';
// import { SerializationPlugin } from 'cs-element/plugins/serialization';

// Система визуализации
export { VisualizationManager } from './visualization/VisualizationManager';
export { AsciiEngine } from './visualization/engines/AsciiEngine';
export { SvgEngine } from './visualization/engines/SvgEngine';
export { HtmlEngine } from './visualization/engines/HtmlEngine';

// Система DevTools
export { DevToolsManager } from './devtools/DevToolsManager';
export { ElementInspector } from './devtools/ElementInspector';
export { PerformanceProfiler } from './devtools/PerformanceProfiler';
export * from './types/devtools-interfaces';

// Система истории изменений
export { HistoryManagerImpl } from './history/HistoryManagerImpl';
export * from './types/history-interfaces';

// Система реактивности и computed свойств
export { ReactivityManagerImpl } from './reactivity/ReactivityManagerImpl';
export * from './types/reactivity-interfaces';

// Система Live queries (реактивных запросов)
export { LiveQueryManagerImpl, LiveQueryBuilderImpl } from './live-queries/LiveQueryManagerImpl';
export * from './types/live-query-interfaces';

// Система алгоритмов работы с графами
export { GraphAlgorithmsManagerImpl } from './graph/GraphAlgorithmsManagerImpl';
export * from './types/graph-interfaces';

// Система State Machine
export { StateMachine } from './state-machine/StateMachine';
export { StateMachineManagerImpl } from './state-machine/StateMachineManager';
export { StateMachineIntegration } from './core/StateMachineIntegration';
export * from './types/state-machine-interfaces';

// Система типизированных элементов
export { TypedElement } from './typed-elements/TypedElement';
export { TypedElementManager, defaultTypedElementManager } from './typed-elements/TypedElementManager';
export * from './types/typed-elements-interfaces';

// Система миграций
export { MigrationManager } from './migration/MigrationManager';
export { MemoryMigrationStorage } from './migration/MemoryMigrationStorage';
export { VersionUtils } from './migration/VersionUtils';
export { MigrationBuilder, createMigration } from './migration/MigrationBuilder';
export { 
  MigrationRegistry, 
  Migration, 
  Up, 
  Down, 
  CanApply, 
  CanRollback, 
  Validate, 
  DependsOn, 
  Type,
  BaseMigration,
  SchemaMigration,
  DataMigration,
  MigrationUtils
} from './migration/MigrationDecorators';
export { MigrationFactory, migrationFactory } from './migration/MigrationFactory';
export { MigrationRunner } from './migration/MigrationRunner';
export { MigrationScheduler } from './migration/MigrationScheduler';
export { MigrationLogger } from './migration/MigrationLogger';
export { MigrationSystem } from './migration/MigrationSystem';
export * from './types/migration-interfaces';

// Реэкспорт для удобства
export { EventEmitter } from 'eventemitter3';

// Для обратной совместимости экспортируем CSElement как ElementBuilder и ElementSpace
export const ElementBuilder = CSElement;
export const ElementSpace = CSElement;

// React Integration
export {
  useElement,
  useQuery,
  useCSElementById,
  useQueryOne,
  useCSElementSystem
} from './react/hooks';

// Web Workers поддержка доступна через отдельный импорт:
// import { WorkerManager } from 'cs-element/workers/nodejs';
export {
  WorkerOperationType,
  WorkerMessage,
  WorkerResponse,
  WorkerError,
  WorkerConfig,
  WorkerPool,
  IWorkerManager,
  WorkerInstance,
  WorkerStatus,
  PoolStatus,

  DiffChange,
  MergeConflict,
  ThreeWayMergeResult,
  ChangeVisualization,
  DiffVisualizationOptions
} from './types/worker-interfaces';

// Diff и Merge система
export { DiffEngine, DiffAlgorithm, MergeStrategy } from './diff/DiffEngine';

// Версия библиотеки
export const VERSION = '2.0.0';

// Инициализация сервисов для избежания циклических зависимостей
import { services } from './core/ServiceRegistry';
services.init(CSElement); 